/**
 * Create/edit dialog for the small admin reference-data tables (warranty fees,
 * RMA fees, warranty statuses). Renders a plain field list plus the shared
 * "Obsolete Version" toggle every one of these lookup rows carries, and hands
 * the assembled values back to the table that owns the mutation. Field-level
 * API errors render inline on their inputs.
 *
 * Contains:
 * - ReferenceCrudDialog — the dialog component
 */
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Filled multiline centers a short string in the box (MuiInputBase-root's
// align-items: center); anchor to the top so typed text lays out from line
// 1 down, matching the pattern SettingsTextField documents.
const MULTILINE_SX = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

function buildInitialValues(entity, fields) {
  const values = {};
  fields.forEach(field => {
    values[field.name] = entity?.[field.name] ?? '';
  });
  return values;
}

function ReferenceCrudDialog({ open, entity, entityLabel, fields, fieldErrors, isSubmitting, onSubmit, onClose }) {
  const { t } = useTranslation();
  const isEdit = Boolean(entity);

  const [values, setValues] = useState(() => buildInitialValues(entity, fields));
  const [obsolete, setObsolete] = useState(Boolean(entity?.obsolete));

  useEffect(() => {
    setValues(buildInitialValues(entity, fields));
    setObsolete(Boolean(entity?.obsolete));
  }, [entity, fields]);

  const handleChange = name => event => setValues(prev => ({ ...prev, [name]: event.target.value }));

  const handleSubmit = () => onSubmit({ ...values, obsolete });

  const title = isEdit
    ? t('admin.crudTable.editDialogTitle', 'Edit {{entityLabel}} Dialog', { entityLabel })
    : t('admin.crudTable.addDialogTitle', 'Add {{entityLabel}} Dialog', { entityLabel });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fields.map(field => (
            <TextField
              key={field.name}
              label={field.label}
              value={values[field.name]}
              onChange={handleChange(field.name)}
              multiline={field.rows > 1}
              minRows={field.rows > 1 ? field.rows : undefined}
              error={Boolean(fieldErrors?.[field.name])}
              helperText={fieldErrors?.[field.name]?.join(' ')}
              fullWidth
              sx={field.rows > 1 ? MULTILINE_SX : undefined}
            />
          ))}
          <FormControlLabel
            control={<Checkbox checked={obsolete} onChange={event => setObsolete(event.target.checked)} />}
            label={t('admin.crudTable.obsoleteVersion', 'Obsolete Version')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" disabled={isSubmitting} onClick={handleSubmit}>
          {t('actions.submit', 'Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ReferenceCrudDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.object,
  entityLabel: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      rows: PropTypes.number.isRequired,
    }),
  ).isRequired,
  fieldErrors: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ReferenceCrudDialog;
